import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
interface HeroSectionProps {
  onCtaClick: () => void;
}
export function HeroSection({
  onCtaClick
}: HeroSectionProps) {
  return <section className="relative flex items-center justify-center py-8 md:py-14 px-4 overflow-hidden bg-mystical" data-id="element-93">
      {/* Background ambient glows */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-[120px] pointer-events-none" data-id="element-94" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gold-light/10 rounded-full blur-[120px] pointer-events-none" data-id="element-95" />

      <div className="max-w-4xl mx-auto flex flex-col items-center relative z-10 text-center" data-id="element-96">
        {/* Pill */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="mb-3 md:mb-5" data-id="element-97">
          <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 border border-gold/30 text-gold font-bold text-[10px] md:text-xs tracking-wider uppercase" data-id="element-98">
            ✨ Manifestación Guiada + Limpieza Energética (Paso a Paso)
          </span>
        </motion.div>

        {/* H1 */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="font-heading text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.1] text-text-primary mb-3 md:mb-4" data-id="element-99">
          Alineá Tu Energía, Decretá con Claridad y Empezá a Manifestar Cambios{' '}
          <span className="text-gold-gradient" data-id="element-100">Reales.</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="font-body-serif text-sm sm:text-base md:text-lg text-text-secondary leading-relaxed max-w-2xl mb-4 md:mb-5" data-id="element-101">
          <span className="font-bold text-text-primary" data-id="element-102">
            Una colección práctica para limpiar bloqueos, cerrar fugas y
            sostener rutinas simples
          </span>{' '}
          que ordenan tu hogar, tu mente y tu energía—para que lo que querés
          atraer tenga base real.
        </motion.p>

        {/* Product Image — arriba, antes de bullets */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="relative w-full max-w-[180px] md:max-w-[300px] mx-auto mb-3 md:mb-4" data-id="element-103">
          <div className="absolute inset-0 bg-gold/15 blur-[35px] md:blur-[55px] rounded-full" data-id="element-104" />
          <img src="https://cdn.magicpatterns.com/uploads/oFv8AdFtSyBBj6PW4PgnaE/ChatGPT_Image_20_mar_2026,_01_06_18_a.m..png" alt="Código de Restauración 24/7" className="relative z-10 w-full h-auto object-contain animate-float drop-shadow-2xl rounded-xl" data-id="element-105" />
        </motion.div>

        {/* Badge */}
        <div className="mb-4 md:mb-5 px-4 py-1.5 rounded-full border border-gold/20 bg-card-bg/80 backdrop-blur-sm shadow-sm" data-id="element-106">
          <span className="text-gold font-bold text-[10px] md:text-xs tracking-wider uppercase" data-id="element-107">
            📦 Colección Completa: 6 Tomos + 6 Bonos Incluidos
          </span>
        </div>

        {/* Bullets — debajo de la imagen */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="w-full max-w-lg mb-4 md:mb-5" data-id="element-108">
          <ul className="space-y-2 text-left" data-id="element-109">
            {[{
            bold: 'Decretos y protocolos listos',
            rest: ' (mañana, noche y urgencias)'
          }, {
            bold: 'Guía por fases',
            rest: ' para destrabar hogar, mente, dinero y hábitos'
          }, {
            bold: 'Herramientas simples',
            rest: ' para sostener el cambio sin depender de nadie'
          }].map((bullet, idx) => <li key={idx} className="flex items-start space-x-2.5" data-id="element-110">
                <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-gold shrink-0 mt-0.5" data-id="element-111" />
                <span className="text-xs md:text-sm text-text-primary leading-snug" data-id="element-112">
                  <span className="font-bold" data-id="element-113">{bullet.bold}</span>
                  {bullet.rest}
                </span>
              </li>)}
          </ul>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.5
      }} className="flex flex-col items-center space-y-1.5 w-full max-w-md" data-id="element-114">
          <button onClick={onCtaClick} className="btn-cta-pulse w-full px-8 py-3.5 md:py-4 rounded-xl text-base md:text-lg tracking-wide font-bold" data-id="element-115">
            QUIERO EMPEZAR HOY
          </button>
          <p className="text-xs md:text-sm text-text-secondary font-medium" data-id="element-116">
            Acceso inmediato • PDF descargable • Empezás en 10 minutos
          </p>
          <p className="text-[10px] md:text-xs text-text-muted italic" data-id="element-117">
            Intención + energía + acción (en el orden correcto).
          </p>
        </motion.div>
      </div>
    </section>;
}